import { FC, Fragment, RefObject, ReactNode } from 'react'
import { NavLink, Link } from 'react-router-dom'
import { Menu, Popover, Transition } from '@headlessui/react'
import { ArrowLongLeftIcon, HomeIcon, UserIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { BellIcon, Cog6ToothIcon, InboxIcon, Bars3Icon } from '@heroicons/react/24/outline'
import classNames from 'classnames'

import { Breadcrumbs, User, AppName } from '../../types'

import useMsgs from '../hooks/useMsgs'
import Message from './Message'
import UserContext from '../modules/User'
import logo from '@/retail/components/Layout/logo.png'

import NotificationDot from './NotificationDot'

const NotificationBadge = ({ count }: { count: number }) => (
  <>
    <span className="absolute -mt-1.5 h-5 w-5 inline-flex items-center justify-center bg-gradient-to-t from-blue-400 to-blue-200 text-blue-900 text-xs rounded-full">
      {/* count -1 means it's loading */}
      {count === -1 ? '' : count > 99 ? '99+' : count}
    </span>
    <span className="w-4 inline-flex" />
  </>
)

// This accounts for the admin dashboard where we can't navigate using the react router
const LinkComponent: FC<{
  to: string
  className?: string
  reactRouterLinks: boolean
  children?: ReactNode
}> = ({ to, className, children, reactRouterLinks }) =>
  reactRouterLinks ? (
    <Link to={to} className={className}>
      {children}
    </Link>
  ) : (
    <a href={to} className={className}>
      {children}
    </a>
  )
const NavLinkComponent: FC<{
  to: string
  className?: string
  activeClassName?: string
  reactRouterLinks: boolean
  children?: ReactNode
}> = ({ to, className, activeClassName, reactRouterLinks, children }) =>
  reactRouterLinks ? (
    <NavLink
      to={to}
      className={({ isActive }) => `${className || ''} ${isActive ? activeClassName : ''}`}
    >
      {children}
    </NavLink>
  ) : (
    <a
      href={to}
      className={classNames(
        className,
        window.location.href.indexOf(to) > -1 ? activeClassName : undefined
      )}
    >
      {children}
    </a>
  )

interface Navigation {
  name: string
  href: string
  // Use -1 to indicate loading
  notificationCount?: number
}
type UnauthenticatedFrameC = FC<{
  children?: ReactNode
  vendorId: string
  userId?: string
  frameRef?: RefObject<HTMLDivElement>
}>
type FrameC = FC<{
  header?: JSX.Element
  breadcrumbs?: Breadcrumbs
  navigation: Navigation[]
  userNavigation: Omit<Navigation, 'notificationCount'>[]
  user: Pick<User, 'role' | 'name' | 'email'>
  reactRouterLinks?: boolean
  showNotificationPageLink: boolean
  hasUnreadMessages?: boolean
  app?: AppName
  orgLogoUrl?: string | null
  frameRef?: RefObject<HTMLDivElement>
  topBanner?: ReactNode
  children?: ReactNode
}> & { Unauthenticated: UnauthenticatedFrameC }

const Frame: FrameC = ({
  header,
  breadcrumbs,
  navigation,
  userNavigation,
  user,
  orgLogoUrl = null,
  reactRouterLinks = true,
  showNotificationPageLink,
  hasUnreadMessages,
  frameRef,
  topBanner,
  children,
  app,
}) => {
  const [msgs, msgsMgr] = useMsgs()

  return (
    <div
      ref={frameRef}
      className="relative flex flex-col min-h-screen bg-gray-100 border-t-8 border-gearflow"
    >
      {topBanner}
      <header className="bg-white shadow">
        <div className="mx-auto px-2 sm:px-4 lg:px-8">
          <Popover className="flex justify-between h-16">
            {({ open }) => (
              <>
                <div className="flex px-2 lg:px-0">
                  <div className="flex-shrink-0 flex items-center py-4">
                    <a href="/">
                      {app === 'admin' ? (
                        <div className="text-l font-bold text-gearflow">Admin</div>
                      ) : (
                        <img src={logo} alt="Gearflow" className="w-36" />
                      )}
                    </a>
                    {orgLogoUrl && (
                      <div className="hidden sm:block max-w-36 h-full ml-4 border-l pl-4">
                        <img
                          src={orgLogoUrl}
                          alt="org logo"
                          className="object-contain h-full w-full"
                        />
                      </div>
                    )}
                  </div>
                  <nav aria-label="Global" className="hidden lg:ml-6 lg:flex lg:items-center">
                    {navigation.map((item) => (
                      <NavLinkComponent
                        to={item.href}
                        key={item.name}
                        activeClassName="bg-gray-100 rounded"
                        className="px-1.5 xl:px-3 py-2 text-gray-900 text-sm block space-x-1 whitespace-nowrap"
                        reactRouterLinks={reactRouterLinks}
                      >
                        <span>{item.name}</span>
                        {typeof item.notificationCount !== 'undefined' &&
                          item.notificationCount !== 0 && (
                            <NotificationBadge count={item.notificationCount} />
                          )}
                      </NavLinkComponent>
                    ))}
                  </nav>
                </div>
                <div className="flex items-center">
                  {app !== 'admin' && UserContext.isAdmin(user) && (
                    <p className="mr-2 ml-2 text-red-500 font-bold">Admin</p>
                  )}
                  {showNotificationPageLink && (
                    <Link
                      className="inline-flex items-center justify-center lg:p-2 p-1 lg:rounded-full rounded-md lg:text-gray-700 lg:hover:text-gray-400 text-gray-400 hover:text-gray-500 lg:hover:bg-transparent hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                      to="/notifications"
                    >
                      <BellIcon className="h-6 w-6" aria-hidden="true" />
                    </Link>
                  )}
                  <Link
                    className="relative inline-flex items-center justify-center lg:p-2 p-1 lg:rounded-full rounded-md lg:text-gray-700 lg:hover:text-gray-400 text-gray-400 hover:text-gray-500 lg:hover:bg-transparent hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                    to="/inbox"
                  >
                    <InboxIcon className="h-6 w-6" aria-hidden="true" />
                    {hasUnreadMessages && (
                      <NotificationDot className="absolute ml-4 mb-4 outline outline-2 outline-offset-0 outline-white" />
                    )}
                  </Link>
                  <div className="flex items-center lg:hidden">
                    {/* Mobile menu button */}
                    <Popover.Button className="inline-flex items-center justify-center lg:p-2 p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                      <span className="sr-only">Open main menu</span>
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    </Popover.Button>
                  </div>
                  <Transition.Root show={open} as={Fragment}>
                    <div className="lg:hidden">
                      <Transition.Child
                        as={Fragment}
                        enter="duration-150 ease-out"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="duration-150 ease-in"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Popover.Overlay
                          static
                          className="z-20 fixed inset-0 bg-black bg-opacity-25"
                          aria-hidden="true"
                        />
                      </Transition.Child>

                      <Transition.Child
                        as={Fragment}
                        enter="duration-150 ease-out"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="duration-150 ease-in"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                      >
                        <Popover.Panel
                          focus
                          static
                          className="z-30 absolute top-0 right-0 max-w-none w-full p-2 transition transform origin-top"
                        >
                          <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-white divide-y divide-gray-200">
                            <div className="pt-3 pb-2">
                              <div className="flex items-center justify-between px-4">
                                <div>Gearflow</div>
                                <div className="-mr-2">
                                  <Popover.Button className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                                    <span className="sr-only">Close menu</span>
                                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                  </Popover.Button>
                                </div>
                              </div>
                              <div className="mt-3 px-2 space-y-1">
                                {navigation.map((item) => (
                                  <LinkComponent
                                    to={item.href}
                                    key={item.name}
                                    className="block rounded-md px-3 py-2 text-base text-gray-900 hover:bg-gray-100 hover:text-gray-800"
                                    reactRouterLinks={reactRouterLinks}
                                  >
                                    {item.name}
                                    {typeof item.notificationCount !== 'undefined' &&
                                      item.notificationCount !== 0 && (
                                        <NotificationBadge count={item.notificationCount} />
                                      )}
                                  </LinkComponent>
                                ))}
                              </div>
                            </div>
                            <div className="pt-4 pb-2">
                              <div className="flex items-center px-5">
                                <div className="flex-shrink-0 bg-gray-200 p-2 rounded-full">
                                  <UserIcon className="flex-shrink-0 h-5 w-5" aria-hidden="true" />
                                </div>
                                <div className="ml-3">
                                  <div className="text-base text-gray-800">{user.name}</div>
                                  <div className="text-sm text-gray-500">{user.email}</div>
                                </div>
                              </div>
                              <div className="mt-3 px-2 space-y-1">
                                {userNavigation.map((item) => (
                                  <a
                                    key={item.name}
                                    href={item.href}
                                    className="block rounded-md px-3 py-2 text-base text-gray-900 hover:bg-gray-100 hover:text-gray-800"
                                  >
                                    {item.name}
                                  </a>
                                ))}
                              </div>
                            </div>
                          </div>
                        </Popover.Panel>
                      </Transition.Child>
                    </div>
                  </Transition.Root>
                  <div className="hidden lg:ml-2 lg:flex lg:items-center">
                    {/* Profile dropdown */}
                    <Menu as="div" className="relative flex-shrink-0">
                      {({ open: opn }) => (
                        <>
                          <div>
                            <Menu.Button className="bg-white rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 bg-gray-200 p-2">
                              <span className="sr-only">Open user menu</span>
                              <UserIcon className="flex-shrink-0 h-5 w-5" aria-hidden="true" />
                            </Menu.Button>
                          </div>
                          <Transition
                            show={opn}
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items
                              static
                              modal={false}
                              className="origin-top-right z-10 absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                            >
                              {userNavigation.map((item) => (
                                <Menu.Item key={item.name}>
                                  <a
                                    href={item.href}
                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-800"
                                  >
                                    {item.name}
                                  </a>
                                </Menu.Item>
                              ))}
                            </Menu.Items>
                          </Transition>
                        </>
                      )}
                    </Menu>
                  </div>
                </div>
              </>
            )}
          </Popover>
        </div>
        {breadcrumbs && (
          <div className="mx-auto px-4 sm:px-6">
            <div className="border-t border-gray-200 py-3">
              <nav className="flex" aria-label="Breadcrumb">
                <div className="flex sm:hidden">
                  <a
                    href="/"
                    className="group inline-flex space-x-3 text-sm text-gray-500 hover:text-gray-700"
                  >
                    <ArrowLongLeftIcon
                      className="flex-shrink-0 h-5 w-5 text-gray-400 group-hover:text-gray-600"
                      aria-hidden="true"
                    />
                    <span>{breadcrumbs.copy}</span>
                  </a>
                </div>
                <div className="hidden sm:block">
                  <ol className="flex items-center space-x-4">
                    <li>
                      <div>
                        <LinkComponent
                          to="/"
                          className="text-gray-400 hover:text-gray-500"
                          reactRouterLinks={reactRouterLinks}
                        >
                          <HomeIcon className="flex-shrink-0 h-5 w-5" aria-hidden="true" />
                          <span className="sr-only">Home</span>
                        </LinkComponent>
                      </div>
                    </li>
                    {breadcrumbs.crumbs.map((item: { href: string; name: string }) => (
                      <li key={item.name}>
                        <div className="flex items-center">
                          <svg
                            className="flex-shrink-0 h-5 w-5 text-gray-300"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            aria-hidden="true"
                          >
                            <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                          </svg>
                          <LinkComponent
                            to={item.href}
                            key={item.name}
                            className="ml-4 text-sm text-gray-500 hover:text-gray-700"
                            reactRouterLinks={reactRouterLinks}
                          >
                            {item.name}
                          </LinkComponent>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </nav>
            </div>
          </div>
        )}
        {header}
      </header>
      {/* region for incoming messages */}
      <div
        aria-live="assertive"
        className="z-30 fixed inset-0 flex flex-col space-y-4 items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start"
      >
        {msgs.length > 0 && (
          <>
            {msgs.map((msg) => (
              <Message msg={msg} removeMsg={msgsMgr.remove} key={msg.id} />
            ))}
          </>
        )}
      </div>
      {children}
    </div>
  )
}

const UnauthenticatedFrame: UnauthenticatedFrameC = ({ children, vendorId, userId, frameRef }) => {
  const [msgs, msgsMgr] = useMsgs()

  return (
    <div
      ref={frameRef}
      className="relative flex flex-col min-h-screen bg-gray-100 border-t-8 border-gearflow"
    >
      <header className="bg-white shadow">
        <div className="mx-auto px-2 sm:px-4 lg:px-8">
          <Popover className="flex justify-between h-16">
            <div className="flex px-2 lg:px-0">
              <div className="flex-shrink-0 flex items-center">
                <a href="/">
                  <img src={logo} alt="Gearflow" className="w-36" />
                </a>
              </div>
            </div>
            <div className="flex items-center">
              <Link
                className="relative inline-flex items-center justify-center lg:p-2 p-1 lg:rounded-full rounded-md lg:text-gray-700 lg:hover:text-gray-400 text-gray-400 hover:text-gray-500 lg:hover:bg-transparent hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                to={
                  userId
                    ? `/contact/${userId}/vendor/${vendorId}/settings`
                    : `/vendor/${vendorId}/settings`
                }
              >
                <Cog6ToothIcon className="h-6 w-6" aria-hidden="true" />
              </Link>
            </div>
          </Popover>
        </div>
      </header>
      {/* region for incoming messages */}
      <div
        aria-live="assertive"
        className="z-30 fixed inset-0 flex flex-col space-y-4 items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start"
      >
        {msgs.length > 0 && (
          <>
            {msgs.map((msg) => (
              <Message msg={msg} removeMsg={msgsMgr.remove} key={msg.id} />
            ))}
          </>
        )}
      </div>
      {children}
    </div>
  )
}

Frame.Unauthenticated = UnauthenticatedFrame
export default Frame
